import 'core-js';
import { ContextType, createInstanceConfig } from '../../contexts/configs/componentConfig';
import { createInitialRegistrationDataState } from '../../contexts/configs/dataContextConfig';
import React, { useEffect, useState } from 'react';
import Sticky from 'react-stickynode';
import { useTranslation } from 'react-i18next';
import './user-registration.scss';

import 'commons/resources/utils/polyfills';
import MetsIcon from 'commons/components/MetsIcon';
import Loader from 'commons/components/Loader';

import 'UserRegistration/resources/i18n/i18n';
import { fetchServerConfiguration, fetchInvitedContactInformation } from 'UserRegistration/resources/api/configurationData';
import SidebarOrderDetails from 'UserRegistration/components/SidebarOrderDetails';
import SidebarUserInfo from 'UserRegistration/components/SidebarUserInfo';
import ConfigurationContext from 'UserRegistration/contexts/ConfigurationContext';
import DataContext from 'UserRegistration/contexts/DataContext';
import { doesRedirectToConfirmationPage } from 'UserRegistration/resources/utils/UserRegistrationStatus';
import { getTranslationSuffix } from 'UserRegistration/resources/utils/translationUtils';
import { updatePersonalInformationContextValue } from 'UserRegistration/resources/utils/personalInfoUtils';

interface UserRegistrationProps {
    context: ContextType,
    preselectedGroupId: number,
    preselectedSubscriptionItemId: [],
    purchasingGroupId: number,
    portalCode: string,
    promoCode: string,
    contactId: number,
    defaultFrequency: string,
    furtherOffersDefaultValue: boolean
}

function UserRegistration(props: UserRegistrationProps) {

    const [componentConfig, setComponentConfig] = useState(createInstanceConfig({
        context: props.context,
        defaultFrequency: props.defaultFrequency,
        preselectedGroupId: props.preselectedGroupId,
        preselectedSubscriptionItemId: props.preselectedSubscriptionItemId,
        purchasingGroupId: props.purchasingGroupId,
        contactId: props.contactId,
        promoCode: props.promoCode,
        portalCode: props.portalCode,
    }));

    const { t } = useTranslation(componentConfig.i18nNamespaces);
    const translationContextSuffix = getTranslationSuffix(props.context);

    useEffect(() => {

        fetchServerConfiguration(props.context).then((data) => {

            const componentConfigCopy: any = Object.assign({}, componentConfig); 

            componentConfigCopy.currencyCode = data.currencyCode;
            componentConfigCopy.supportPhonenumber = data.supportPhonenumber;
            componentConfigCopy.supplierEmailAsUsername = data.supplierEmailAsUsername;

            componentConfigCopy.hidePromoCodeInput = data.hidePromoCodeInput;

            componentConfigCopy.settings = data.settings;

            data.components.forEach((config: any, index: any) => {

                const componentName = config.componentName;

                componentConfigCopy.settings = data.settings;

                // If there is no known component base definition, throw an error.
                if (!componentConfigCopy.componentDefinition[componentName]) {
                    console.error('Unrecognized component name: ' + componentName);
                    return;
                }

                componentConfigCopy.activeComponents.push(componentName);
                componentConfigCopy.applicableComponents.push(componentName);

                componentConfigCopy.componentDefinition[componentName].sequence = (index + 1);
                componentConfigCopy.componentDefinition[componentName].props = {
                    ...componentConfigCopy.componentDefinition[componentName].props,
                    ...config.parameters
                };

            });

            // MERX specific convenience flag that determines whether or not the Private Construction module is loaded.
            componentConfigCopy.isPCEnabled = componentConfigCopy.componentDefinition['PublicPackageSelector'].props.subComponents.find((subComponent: any) => {
                return subComponent.componentName === 'PCSubscriptionSelector';
            }) || componentConfigCopy.componentDefinition['AddContactPackageSelector'].props.subComponents?.find((subComponent: any) => {
                return subComponent.componentName === 'PCSubscriptionSelector';
            }) ? true : false;

            if (props.contactId) {
                fetchInvitedContactInformation(props.contactId).then((data) => {
                    if (data.contactId) {
                        updatePersonalInformationContextValue("organizationName", data.organizationName, registrationData, setRegistrationData);
                        updatePersonalInformationContextValue("userFirstName", data.firstName, registrationData, setRegistrationData);
                        updatePersonalInformationContextValue("userLastName", data.lastName, registrationData, setRegistrationData);
                        if (componentConfigCopy.supplierEmailAsUsername) {
                            updatePersonalInformationContextValue("emailUsername", data.email, registrationData, setRegistrationData);
                        } else {
                            updatePersonalInformationContextValue("emailAddress", data.email, registrationData, setRegistrationData);
                        }
                    }
                });
            }

            setComponentConfig(componentConfigCopy);

        });    

    }, []);

    /**
     * The registrationData contains all of the common data that is used throughout the UserRegistration component.
     * This data is access through a context because most of the data is used from many different places in 
     * the code. Notably, the registrationData contains the registrationModel, which contains the captured data that is 
     * entered by the user. The declaration below initializes the registrationData with the default values and registers it in 
     * React's state handling.
     */
    const [registrationData, setRegistrationData] = useState<any>(createInitialRegistrationDataState(props.context, props.furtherOffersDefaultValue));
        
    const renderServerErrors = () => {
        
        if (registrationData.validationErrors && registrationData.validationErrors.length > 0) {

            return (
                <div className="error-messages">
                    <MetsIcon name="svg_error" />
                    {registrationData.validationErrors.map((error: any, index: any) => 
                        <div className="error" key={index}>{error.defaultMessage}</div>)}

                </div>
            );

        }

    };

    return (
        <>

            <ConfigurationContext.Provider value={{componentConfig, setComponentConfig}}>
                <DataContext.Provider value={{registrationData, setRegistrationData}}>
                    

                        {!doesRedirectToConfirmationPage(registrationData.metadata.status) && <> 
                        
                            <div className="registration-frame">

                                <div className="registration-frame-inner-wrapper">
                                    <div className="registration-frame-inner">

                                        {renderServerErrors()}                      

                                        <h1>{ t('pageHeading' + translationContextSuffix) }</h1>

                                        {componentConfig.applicableComponents.map((componentName) => {

                                            const componentDefinition: any = componentConfig.componentDefinition[componentName];

                                            // For each active component, create an instance based on the associated component definition.
                                            return React.createElement(
                                                componentDefinition.component,
                                                {
                                                    ...componentDefinition.props,
                                                    key: componentName,
                                                    sectionCount: componentConfig.activeComponents.length,
                                                    sectionNumber: componentDefinition.sequence
                                                }
                                            );

                                        })}

                                    </div>
                                </div>
                            </div>

                            <div className="registration-sidebar">
                                <Sticky top={60} bottomBoundary='.registration-sidebar'>
                                    <div className="registration-sidebar-inner">
                                        <SidebarOrderDetails />
                                        <SidebarUserInfo />
                                    </div>
                                </Sticky>
                            </div> 
                        
                            {/* Show the loading animation when the form is submitting */}
                            {registrationData?.metadata.isSubmitting && <Loader appendTo="body" loadingText={t('submittingForm')}></Loader>}

                        </>}
                        
                        {doesRedirectToConfirmationPage(registrationData.metadata.status) && 
                            <div className="registration-confirmation">
                                {React.createElement(componentConfig.confirmationPage)}
                            </div>
                        }
                    
                </DataContext.Provider>
            </ConfigurationContext.Provider>

        </>
    );

}

export default UserRegistration;
